(ns hypnos.translator.catalog
  "Translators between Clojure and GraphQL for the catalog models."
  (:require [clojure.spec.alpha :as s]
            [clojure.string :as str]
            [medley.core :as mc]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Specs

;; status
(def ^{:const true :private true} statuses #{:status/active :status/inactive})
(s/def ::status statuses)
(s/def ::status-gql (set (map name statuses)))

;; supply-type
(def ^{:const true :private true} supply-types #{:supply/mask})
(s/def ::supply-type supply-types)
(s/def ::supply-type-gql (set (map name supply-types)))

;; mask-category
(def ^{:const true :private true} mask-categories
  #{:mask/full-face
    :mask/hybrid
    :mask/nasal
    :mask/nasal-pillow
    :mask/nasal-prong
    :mask/oral
    :mask/total-face
    :mask/unknown})
(s/def ::mask-category mask-categories)
(s/def ::mask-category-gql (set (map name mask-categories)))

;; mask-size
(def ^{:const true :private true} mask-sizes
  #{:mask-size/pediatric
    :mask-size/petite
    :mask-size/extra-small
    :mask-size/small
    :mask-size/small-wide
    :mask-size/medium-small
    :mask-size/medium-shallow
    :mask-size/medium-wide
    :mask-size/medium
    :mask-size/standard
    :mask-size/standard-wide
    :mask-size/standard-shallow
    :mask-size/standard-deep
    :mask-size/regular
    :mask-size/large
    :mask-size/large-shallow
    :mask-size/large-wide
    :mask-size/extra-large
    :mask-size/fit-kit
    :mask-size/multi
    :mask-size/unknown})
(s/def ::mask-size mask-sizes)
(s/def ::mask-size-gql (set (map name mask-sizes)))

;; mask-gender
(def ^{:const true :private true} mask-genders
  #{:mask-gender/female
    :mask-gender/male
    :mask-gender/universal})
(s/def ::mask-gender mask-genders)
(s/def ::mask-gender-gql (set (map name mask-genders)))

;; manufacturer
(s/def :manufacturer/id uuid?)
(s/def :manufacturer/key string?)
(s/def :manufacturer/name string?)
(s/def :manufacturer/status ::status)

(s/def :manufacturer.gql/id string?)
(s/def :manufacturer.gql/key string?)
(s/def :manufacturer.gql/name string?)
(s/def :manufacturer.gql/status ::status-gql)

(s/def :hypnos/manufacturer
  (s/keys :req [:manufacturer/id
                :manufacturer/key
                :manufacturer/name
                :manufacturer/status]))

(s/def :hypnos.gql/manufacturer
  (s/keys :req-un [:manufacturer.gql/id
                   :manufacturer.gql/key
                   :manufacturer.gql/name
                   :manufacturer.gql/status]))

;; make

(s/def :make/id uuid?)
(s/def :make/type ::supply-type)
(s/def :make/category ::mask-category)
(s/def :make/name string?)
(s/def :make/fit-guide string?)
(s/def :make/manufacturer :hypnos/manufacturer)
(s/def :make/status ::status)

(s/def :make.gql/id string?)
(s/def :make.gql/type ::supply-type-gql)
(s/def :make.gql/category ::mask-category-gql)
(s/def :make.gql/name string?)
(s/def :make.gql/fit-guide string?)
(s/def :make.gql/manufacturer :hypnos.gql/manufacturer)
(s/def :make.gql/status ::status-gql)

(s/def :hypnos/make
  (s/keys :req [:make/id
                :make/type
                :make/category
                :make/name
                :make/manufacturer
                :make/status]
          :opt [:make/fit-guide]))

(s/def :hypnos.gql/make
  (s/keys :req-un [:make.gql/id
                   :make.gql/type
                   :make.gql/category
                   :make.gql/name
                   :make.gql/manufacturer
                   :make.gql/status]
          :opt-un [:make.gql/fit-guide]))

;; model

(s/def :model/id uuid?)
(s/def :model/make :hypnos/make)
(s/def :model/name string?)
(s/def :model/description string?)
(s/def :model/gender ::mask-gender)
(s/def :model/size ::mask-size)
(s/def :model/sku string?)
(s/def :model/status ::status)

(s/def :model.gql/id string?)
(s/def :model.gql/make :hypnos.gql/make)
(s/def :model.gql/name string?)
(s/def :model.gql/description string?)
(s/def :model.gql/gender ::mask-gender-gql)
(s/def :model.gql/size ::mask-size-gql)
(s/def :model.gql/sku string?)
(s/def :model.gql/status ::status-gql)

(s/def :hypnos/model
  (s/keys :req [:model/id
                :model/make
                :model/name
                :model/description
                :model/gender
                :model/size
                :model/sku
                :model/status]))

(s/def :hypnos.gql/model
  (s/keys :req-un [:model.gql/id
                   :model.gql/make
                   :model.gql/name
                   :model.gql/description
                   :model.gql/gender
                   :model.gql/size
                   :model.gql/sku
                   :model.gql/status]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Enums

;; status
(defn status->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "status/" s))))

(s/fdef status->clj
  :args (s/cat :s (s/or :status ::status-gql :nil nil?))
  :ret (s/or :status ::status :nil nil?))

(defn status->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef status->gql
  :args (s/cat :o (s/or :status ::status :nil nil?))
  :ret (s/or :status ::status-gql :nil nil?))

;; supply-type

(defn supply-type->clj
  [st]
  (when-not (str/blank? st)
    (keyword (str "supply/" st))))

(s/fdef supply-type->clj
  :args (s/cat :s (s/or :supply-type ::supply-type-gql :nil nil?))
  :ret (s/or :supply-type ::supply-type :nil nil?))

(defn supply-type->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef supply-type->gql
  :args (s/cat :o (s/or :supply-type ::supply-type :nil nil?))
  :ret (s/or :supply-type ::supply-type-gql :nil nil?))

;; mask-category

(defn mask-category->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "mask/" (str/replace s #"_" "-")))))

(s/fdef mask-category->clj
  :args (s/cat :s (s/or :mask-category ::mask-category-gql :nil nil?))
  :ret (s/or :mask-category ::mask-category :nil nil?))

(defn mask-category->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef mask-category->gql
  :args (s/cat :o (s/or :mask-category ::mask-category :nil nil?))
  :ret (s/or :mask-category ::mask-category-gql :nil nil?))

;; mask-size

(defn mask-size->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "mask-size/" (str/replace s #"_" "-")))))

(s/fdef mask-size->clj
  :args (s/cat :s (s/or :mask-size ::mask-size-gql :nil nil?))
  :ret (s/or :mask-size ::mask-size :nil nil?))

(defn mask-size->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef mask-size->gql
  :args (s/cat :o (s/or :mask-size ::mask-size :nil nil?))
  :ret (s/or :mask-size ::mask-size-gql :nil nil?))

;; mask-gender

(defn mask-gender->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "mask-gender/" s))))

(s/fdef mask-gender->clj
  :args (s/cat :s (s/or :mask-gender ::mask-gender-gql :nil nil?))
  :ret (s/or :mask-gender ::mask-gender :nil nil?))

(defn mask-gender->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef mask-gender->gql
  :args (s/cat :o (s/or :mask-gender ::mask-gender :nil nil?))
  :ret (s/or :mask-gender ::mask-gender-gql :nil nil?))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Entities

(declare make->clj make->gql model->clj model->gql)

(defn manufacturer->clj
  [m]
  (when m
    (cond-> {}
      (:id m) (assoc :manufacturer/id (mc/uuid (:id m)))
      (:key m) (assoc :manufacturer/key (:key m))
      (:name m) (assoc :manufacturer/name (:name m))
      (:makes m) (assoc :make/_manufacturer (mapv make->clj (:makes m))))))

(s/fdef manufacturer->clj
  :args (s/cat :m (s/or :manufacturer :hypnos.gql/manufacturer :nil nil?))
  :ret (s/or :manufacturer (s/and map? :hypnos/manufacturer) :nil nil?))

(defn manufacturer->gql
  [m]
  (when m
    (cond-> {}
      (:manufacturer/id m) (assoc :id (str (:manufacturer/id m)))
      (:manufacturer/key m) (assoc :key (:manufacturer/key m))
      (:manufacturer/name m) (assoc :name (:manufacturer/name m))
      (:make/_manufacturer m) (assoc :makes (mapv make->gql (:make/_manufacturer m))))))

(s/fdef manufacturer->gql
  :args (s/cat :m (s/or :manufacturer :hypnos/manufacturer :nil nil?))
  :ret (s/or :manufacturer (s/and map? :hypnos.gql/manufacturer) :nil nil?))

(defn make->clj
  [m]
  (when m
    (cond-> {}
      (:id m) (assoc :make/id (mc/uuid (:id m)))
      (:category m) (assoc :make/category (mask-category->clj (:category m)))
      (:name m) (assoc :make/name (:name m))
      (:fit_guide m) (assoc :make/fit-guide (:fit_guide m))
      (:models m) (assoc :model/_make (mapv model->clj (:models m))))))

(s/fdef make->clj
  :args (s/cat :m (s/or :make :hypnos.gql/make :nil nil?))
  :ret (s/or :make (s/and map? :hypnos/make :nil nil?)))

(defn make->gql
  [m]
  (when m
    (cond-> {}
      (:make/id m) (assoc :id (str (:make/id m)))
      (:make/category m) (assoc :category (mask-category->gql (:make/category m)))
      (:make/name m) (assoc :name (:make/name m))
      (:make/fit-guide m) (assoc :fit_guide (:make/fit-guide m))
      (:model/_make m) (assoc :models (mapv model->gql (:model/_make m))))))

(s/fdef make->gql
  :args (s/cat :m (s/or :make :hypnos/make :nil nil?))
  :ret (s/or :make (s/and map? :hypnos.gql/make) :nil nil?))

(defn model->clj
  [m]
  (when m
    (cond-> {}
      (:id m) (assoc :model/id (mc/uuid (:id m)))
      (:name m) (assoc :model/name (:name m))
      (:description m) (assoc :model/description (:description m))
      (:gender m) (assoc :model/gender (mask-gender->clj (:gender m)))
      (:size m) (assoc :model/size (mask-size->clj (:size m)))
      (:sku m) (assoc :model/sku (:sku m)))))

(s/fdef model->clj
  :args (s/cat :m (s/or :model :hypnos.gql/model :nil nil?))
  :ret (s/or :model (s/and map? :hypnos/model) :nil nil?))

(defn model->gql
  [m]
  (when m
    (cond-> {}
      (:model/id m) (assoc :id (str (:model/id m)))
      (:model/name m) (assoc :name (:model/name m))
      (:model/description m) (assoc :description (:model/description m))
      (:model/gender m) (assoc :gender (mask-gender->gql (:model/gender m)))
      (:model/size m) (assoc :size (mask-size->gql (:model/size m)))
      (:model/sku m) (assoc :sku (:model/sku m)))))

(s/fdef model->gql
  :args (s/cat :m (s/or :model :hypnos/model :nil nil?))
  :ret (s/or :model (s/and map? :hypnos.gql/model) :nil nil?))
